import React, { useState, useMemo } from 'react';
import {
  Search, Loader2, DollarSign, ArrowRightLeft, Calendar, Database,
  TrendingUp, BarChart3, Activity, ShieldAlert, Package, ChevronRight,
} from 'lucide-react';
import {
  BarChart, Bar, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  useConsumerTransactions,
  useConsumerTransactionSummary,
  useConsumerTransactionItemDetails,
} from '../hooks/useConsumerTransactions';
import DataTable from '../components/DataTable';
import { formatDateTime, formatCurrency, formatNumber } from '../utils/formatters';

const CHART_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const BENEFIT_TYPE_LABELS = {
  '5D': 'Food (5D)',
  '5I': 'Insulin (5I)',
  '5A': 'OTC (5A)',
};

function formatBenefitType(code) {
  if (!code) return null;
  return BENEFIT_TYPE_LABELS[code] || code;
}

function formatElapsedMinutes(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';

  const totalMinutes = Math.max(0, Number(value));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
  return parts.join(' ');
}

function TransactionItemDetails({ consumerCode, transactionId, enabled }) {
  const { data, isLoading, isError, error } = useConsumerTransactionItemDetails(
    consumerCode,
    transactionId,
    enabled
  );

  if (!enabled) {
    return null;
  }

  const items = data?.data || [];

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-white">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <Package className="w-4 h-4 text-primary-600" />
        <h4 className="text-sm font-semibold text-gray-900">Item-Level Details</h4>
        {items.length > 0 && (
          <span className="text-xs text-gray-400">({formatNumber(items.length)} rows)</span>
        )}
      </div>

      <div className="p-4">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading item-level details...
          </div>
        )}

        {isError && (
          <div className="text-sm text-red-600">
            Failed to load item-level details: {error?.message || 'Unknown error'}
          </div>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <p className="text-sm text-gray-500">No item-level details found for this transaction.</p>
        )}

        {!isLoading && !isError && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm table-fixed">
              <colgroup>
                <col className="w-[24%]" />
                <col className="w-[10%]" />
                <col className="w-[6%]" />
                <col className="w-[10%]" />
                <col className="w-[10%]" />
                <col className="w-[16%]" />
                <col className="w-[8%]" />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Product</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Category</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">Qty</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">Unit Price</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">Total</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Benefit / Funding</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">APL</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {items.map((item, index) => (
                  <tr key={`${transactionId}-${index}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900 break-words">{item.product_description || item.product_identifier || 'N/A'}</div>
                      {item.product_identifier && (
                        <div className="text-xs text-gray-400 break-words">{item.product_identifier}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {[item.product_category_1, item.product_category_2, item.product_category_3].filter(Boolean).join(' / ') || '-'}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-900">{formatNumber(item.quantity || 0)}</td>
                    <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(item.unit_price || 0)}</td>
                    <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(item.total_amount || 0)}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {[formatBenefitType(item.benefit_type), item.funding_status].filter(Boolean).join(' / ') || '-'}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {item.is_on_apl === true ? 'Yes' : item.is_on_apl === false ? 'No' : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TransactionExpandedContent({ row }) {
  const detailItemsEnabled = row.has_item_details && !!row.consumer_code && !!row.item_detail_transaction_id;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-xs uppercase tracking-wide text-gray-400">Posting Date</p>
          <p className="mt-1 text-sm font-medium text-gray-900">{formatDateTime(row.post_date_time)}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-xs uppercase tracking-wide text-gray-400">Elapsed Time</p>
          <p className="mt-1 text-sm font-medium text-gray-900">{formatElapsedMinutes(row.elapsed_minutes)}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-xs uppercase tracking-wide text-gray-400">Rail</p>
          <p className="mt-1 text-sm font-medium text-gray-900">{row.rail_type || row.source_description || '-'}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-xs uppercase tracking-wide text-gray-400">Response</p>
          <p className="mt-1 text-sm font-medium text-gray-900">{row.response_description || '-'}</p>
        </div>
      </div>

      {row.is_declined && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <ShieldAlert className="w-4 h-4" />
            <h4 className="text-sm font-semibold">Decline Details</h4>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-red-500">Response</p>
              <p className="font-medium text-red-900">{row.response_description || '-'}</p>
            </div>
            <div>
              <p className="text-red-500">Reason</p>
              <p className="font-medium text-red-900">{row.reason_code_description || '-'}</p>
            </div>
          </div>
        </div>
      )}

      <TransactionItemDetails
        consumerCode={row.consumer_code}
        transactionId={row.item_detail_transaction_id}
        enabled={detailItemsEnabled}
      />
    </div>
  );
}

export default function ConsumerFinancialTransactions() {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [showVisualizations, setShowVisualizations] = useState(false);

  const filters = useMemo(() => ({
    transactionType: filterType || undefined,
    dataSource: filterSource || undefined,
    showAdmin,
  }), [filterType, filterSource, showAdmin]);

  const { data: txnData, isLoading, isError, error } = useConsumerTransactions(searchTerm, filters);
  const { data: summaryData, isLoading: summaryLoading } = useConsumerTransactionSummary(searchTerm, filters);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setSearchTerm(searchInput.trim());
      setFilterType('');
      setFilterSource('');
    }
  };

  const railIndicators = useMemo(() => {
    const rows = txnData?.data || [];
    const uniqueRails = [...new Set(
      rows
        .map((row) => row.rail_type || row.source_description)
        .filter(Boolean)
    )];

    return uniqueRails;
  }, [txnData]);

  const summary = useMemo(() => {
    if (!summaryData?.data) return null;
    const rows = summaryData.data;
    const totals = rows.find((r) => r.section === 'totals');
    const byType = rows.filter((r) => r.section === 'by_type').map((r) => ({
      name: r.label,
      count: parseInt(r.count) || 0,
      amount: parseFloat(r.amount) || 0,
    }));
    const bySource = rows.filter((r) => r.section === 'by_source').map((r) => ({
      name: r.label,
      count: parseInt(r.count) || 0,
      amount: parseFloat(r.amount) || 0,
    }));
    const byMonth = rows
      .filter((r) => r.section === 'by_month')
      .map((r) => ({
        month: new Date(r.label).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
        sortKey: r.label,
        count: parseInt(r.count) || 0,
        amount: parseFloat(r.amount) || 0,
        netPurchases: parseFloat(r.origin_amount) || 0,
      }))
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey));

    let runningTotal = 0;
    const cumulativeData = byMonth.map((m) => {
      runningTotal += m.netPurchases;
      return { ...m, cumulative: runningTotal };
    });

    const byEntity = rows
      .filter((r) => r.section === 'by_entity')
      .map((r) => ({
        name: r.label,
        originAmount: parseFloat(r.origin_amount) || 0,
        destAmount: parseFloat(r.dest_amount) || 0,
        originCount: parseInt(r.origin_count) || 0,
        destCount: parseInt(r.dest_count) || 0,
      }))
      .sort((a, b) => (b.originAmount + b.destAmount) - (a.originAmount + a.destAmount))
      .slice(0, 15);

    return {
      totalTransactions: parseInt(totals?.count) || 0,
      totalAmount: parseFloat(totals?.amount) || 0,
      netPurchases: parseFloat(totals?.origin_amount) || 0,
      uniqueTypes: parseInt(totals?.unique_types) || 0,
      earliest: totals?.earliest_transaction,
      latest: totals?.latest_transaction,
      byType,
      bySource,
      byMonth: cumulativeData,
      byEntity,
    };
  }, [summaryData]);

  const availableTypes = useMemo(() => {
    if (!summary?.byType) return [];
    return summary.byType.map((t) => t.name).sort();
  }, [summary]);

  const availableSources = useMemo(() => {
    if (!summary?.bySource) return [];
    return summary.bySource.map((s) => s.name).sort();
  }, [summary]);

  const columns = [
    {
      key: 'date_time',
      label: 'Date',
      sortable: true,
      render: (value) => formatDateTime(value),
    },
    {
      key: 'transaction_type',
      label: 'Type',
      sortable: true,
      render: (value, row) => (
        <div className="max-w-[10rem] break-words">
          <span className="block">{value || '-'}</span>
          {row.is_declined && (
            <span className="inline-flex mt-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700">
              Declined
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'transaction_description',
      label: 'Description',
      sortable: false,
      render: (value) => (
        <span className="max-w-[16rem] break-words block">{value || '-'}</span>
      ),
    },
    {
      key: 'origin_display_name',
      label: 'Origin',
      sortable: true,
      sortValue: (value) => value || '',
      render: (value) => (
        <span className="max-w-[12rem] break-words block">{value || '-'}</span>
      ),
    },
    {
      key: 'destination_display_name',
      label: 'Destination',
      sortable: true,
      sortValue: (value) => value || '',
      render: (value) => (
        <span className="max-w-[12rem] break-words block">{value || '-'}</span>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      format: 'decimal',
      sortValue: (value) => parseFloat(value || 0),
    },
    { key: 'currency', label: 'Currency', sortable: true },
    { key: 'data_source', label: 'Data Source', sortable: true },
  ];

  const tooltipStyle = {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Financial Transactions</h2>
        <p className="text-gray-500 mt-1">
          Search a consumer to view their complete transaction history across SUNNY, FIS card transactions, and FIS value loads.
        </p>
      </div>

      {/* Search */}
      <div className="card">
        <h3 className="card-header flex items-center gap-2">
          <Search className="w-5 h-5 text-primary-600" />
          Consumer Search
        </h3>
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Enter Consumer Code or Member ID..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            type="submit"
            disabled={!searchInput.trim() || isLoading}
            className="btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Search
          </button>
        </form>
      </div>

      {/* Error state */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load transactions: {error?.message || 'Unknown error'}
        </div>
      )}

      {/* Loading state for summary */}
      {searchTerm && summaryLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <span className="ml-3 text-gray-500">Loading transaction data...</span>
        </div>
      )}

      {searchTerm && railIndicators.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <p className="font-medium">Detected rails: {railIndicators.join(', ')}</p>
          <p className="mt-1 text-blue-700">
            When VISA rail activity is present, use MCC and filtered spend context rather than raw amount alone.
          </p>
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card flex items-start gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(summary.totalTransactions)}</p>
            </div>
          </div>
          <div className="card flex items-start gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Net Purchases</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.netPurchases)}</p>
              <p className="text-xs text-gray-400">Purchases minus Returns</p>
            </div>
          </div>
          <div className="card flex items-start gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Database className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Transaction Types</p>
              <p className="text-2xl font-bold text-gray-900">{summary.uniqueTypes}</p>
            </div>
          </div>
          <div className="card flex items-start gap-3">
            <div className="p-2 bg-amber-50 rounded-lg">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Date Range</p>
              <p className="text-sm font-semibold text-gray-900">
                {summary.earliest
                  ? `${new Date(summary.earliest).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} — ${new Date(summary.latest).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
                  : '-'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Visualizations */}
      {summary && summary.totalTransactions > 0 && (
        <div className="space-y-6">
          <div className="card">
            <button
              type="button"
              onClick={() => setShowVisualizations((current) => !current)}
              className="flex w-full items-center justify-between gap-4 text-left"
            >
              <div>
                <h3 className="card-header flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary-600" />
                  Visualizations
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Transaction charts start minimized by default. Expand to review trends and account flow.
                </p>
              </div>
              <span className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700">
                {showVisualizations ? 'Hide charts' : 'Show charts'}
                <ChevronRight className={`h-4 w-4 transition-transform ${showVisualizations ? 'rotate-90' : ''}`} />
              </span>
            </button>
          </div>

          {showVisualizations && (
            <>
              {/* Money Flow by Account */}
              {summary.byEntity.length > 0 && (
                <div className="card">
                  <h3 className="card-header flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary-600" />
                    Money Flow by Account
                    <span className="text-sm font-normal text-gray-400 ml-1">(top 15 by total amount)</span>
                  </h3>
                  <div style={{ height: Math.max(300, summary.byEntity.length * 36 + 60) }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={summary.byEntity}
                        layout="vertical"
                        margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} tickFormatter={(v) => formatCurrency(v)} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          tick={{ fontSize: 11 }}
                          tickLine={false}
                          width={160}
                        />
                        <Tooltip
                          contentStyle={tooltipStyle}
                          formatter={(value, name) => [formatCurrency(value), name]}
                        />
                        <Legend />
                        <Bar dataKey="originAmount" name="Sent (Origin)" fill="#ef4444" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="destAmount" name="Received (Destination)" fill="#22c55e" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Amount by Transaction Type */}
              <div className="card">
                <h3 className="card-header flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary-600" />
                  Amount by Transaction Type
                </h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={summary.byType} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} tickLine={false} angle={-20} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 12 }} tickLine={false} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="amount" name="Amount" radius={[4, 4, 0, 0]}>
                        {summary.byType.map((_, index) => (
                          <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Cumulative Amount Over Time */}
              <div className="card">
                <h3 className="card-header flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary-600" />
                  Cumulative Net Purchases Over Time
                </h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={summary.byMonth} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 12 }} tickLine={false} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="cumulative"
                        name="Cumulative Net Purchases"
                        stroke="#8b5cf6"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="netPurchases"
                        name="Monthly Net Purchases"
                        stroke="#22c55e"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Filters + Table */}
      {searchTerm && (
        <>
          {/* Filters */}
          <div className="card">
            <h3 className="card-header flex items-center gap-2">
              <Search className="w-5 h-5 text-primary-600" />
              Filters
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">All Types</option>
                  {availableTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Source</label>
                <select
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">All Sources</option>
                  {availableSources.map((source) => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-3 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 w-full">
                  <input
                    type="checkbox"
                    checked={showAdmin}
                    onChange={(e) => setShowAdmin(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>Show admin / internal transfer rows</span>
                </label>
              </div>
            </div>
          </div>

          {/* Data Table */}
          <div className="card">
            <h3 className="card-header flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-primary-600" />
              Transaction Details
              {txnData?.data && (
                <span className="text-sm font-normal text-gray-400 ml-2">
                  ({formatNumber(txnData.data.length)} rows)
                </span>
              )}
            </h3>
            <p className="mb-4 text-sm text-gray-500">
              Use <span className="font-medium text-gray-700">View items</span> on eligible rows to open the item-level transaction detail panel.
            </p>
            <DataTable
              data={txnData?.data || []}
              columns={columns}
              loading={isLoading}
              getRowId={(row, index) => `${row.master_transaction_id || 'txn'}-${index}`}
              isRowExpandable={(row) => Boolean(
                row.post_date_time
                || row.source_description
                || row.is_declined
                || row.has_item_details
              )}
              getExpandButtonLabel={(row, expanded) =>
                expanded ? 'Hide details' : 'View details'
              }
              renderExpandedContent={(row) => <TransactionExpandedContent row={row} />}
              rowClassName={(row) => (row.is_declined ? 'bg-red-50/40' : '')}
              emptyMessage="No transactions found for this consumer with the selected filters"
            />
          </div>
        </>
      )}

      {/* Empty state before search */}
      {!searchTerm && (
        <div className="text-center py-16">
          <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-500">Search for a consumer</h3>
          <p className="text-gray-400 mt-1">Enter a consumer code above to view their financial transaction history.</p>
        </div>
      )}
    </div>
  );
}
